import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, UserCog, Trash2, Loader2, Pencil, Save, X, ChevronDown, Check, Search, Link2, Unlink, Users2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { useEscapeHierarchy } from '../../hooks/useKeyboardShortcuts';
import { useCardNavigation } from '../../hooks/useCardNavigation';
import { BulkImportModal } from '../ui/BulkImportModal';

const COLORS = [
  { value: 'blue', hex: '#4285f4' }, { value: 'violet', hex: '#7c3aed' },
  { value: 'rose', hex: '#e11d48' }, { value: 'emerald', hex: '#059669' },
  { value: 'orange', hex: '#ea580c' }, { value: 'amber', hex: '#d97706' },
  { value: 'indigo', hex: '#4f46e5' }, { value: 'teal', hex: '#0d9488' },
  { value: 'pink', hex: '#db2777' }, { value: 'slate', hex: '#64748b' },
  { value: 'cyan', hex: '#06b6d4' }, { value: 'lime', hex: '#84cc16' },
  { value: 'yellow', hex: '#facc15' }, { value: 'red', hex: '#ef4444' },
  { value: 'purple', hex: '#a855f7' }, { value: 'fuchsia', hex: '#d946ef' },
  { value: 'green', hex: '#22c55e' },
];

export default function AssignmentsPanel() {
  const [assignments, setAssignments] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  
  // Inline Form States
  const [addingNew, setAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    faculty_id: '',
    subject_id: '',
    batch_ids: [], // for multi-select checklist in 'add' mode
    batch_id: '',  // for single select in 'edit' mode
    hours_per_week: 3,
    min_slot_hours: 1.0,
    max_slot_hours: 2.0,
    color: 'blue',
    group_id: ''
  });
  const [showGroupManager, setShowGroupManager] = useState(false);

  const fetchData = async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const teamId = profile.team_id;

      if (!teamId) {
        setLoading(false);
        return;
      }

      const [asgRes, facRes, subRes, batRes, brRes] = await Promise.all([
        supabase.from('faculty_assignments').select('*').eq('team_id', teamId),
        supabase.from('faculty').select('id, name, max_hours_per_day').eq('team_id', teamId),
        supabase.from('subjects').select('id, name, credit_hours').eq('team_id', teamId),
        supabase.from('batches').select('id, name, code, branch_id').eq('team_id', teamId),
        supabase.from('branches').select('id, name').eq('team_id', teamId),
      ]);
      
      if (asgRes.error) throw asgRes.error;
      if (facRes.error) throw facRes.error;
      if (subRes.error) throw subRes.error;
      if (batRes.error) throw batRes.error;
      if (brRes.error) throw brRes.error;
      
      const fetchedFaculty = facRes.data || [];
      const fetchedSubjects = subRes.data || [];
      const fetchedBatches = batRes.data || [];
      
      const assignments = (asgRes.data || []).map(a => {
        const fac = fetchedFaculty.find(f => f.id === a.faculty_id);
        const sub = fetchedSubjects.find(s => s.id === a.subject_id);
        const bat = fetchedBatches.find(b => b.id === a.batch_id);
        return {
          ...a,
          faculty_name: fac?.name,
          faculty_department: fac?.department,
          subject_name: sub?.name,
          batch_name: bat?.name,
          batch_code: bat?.code,
          group_id: a.group_id || null,
        };
      });
      
      setAssignments(assignments);
      setFaculty(facRes.data || []);
      setSubjects(subRes.data || []);
      setBatches(batRes.data || []);
      setBranches(brRes.data || []);
    } catch (err) { 
      toast.error('Failed to load assignments'); 
      console.error(err); 
    }
    setLoading(false);
  };

  useEffect(() => { 
    fetchData(); 
  }, []);

  const save = async () => {
    if (!form.faculty_id || !form.subject_id || form.batch_ids.length === 0) { 
      toast.error('All fields required'); 
      return; 
    }
    const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
    try {
      const inserts = form.batch_ids.map(bid => ({
        faculty_id: form.faculty_id,
        subject_id: form.subject_id,
        batch_id: bid,
        hours_per_week: form.hours_per_week,
        min_slot_hours: form.min_slot_hours,
        max_slot_hours: form.max_slot_hours,
        color: form.color,
        group_id: form.group_id?.trim() || null,
        team_id: profile.team_id
      }));

      const { error } = await supabase
        .from('faculty_assignments')
        .insert(inserts);
      if (error) throw error;
      
      await fetchData();
      toast.success(`Created ${inserts.length} assignments`);
      setAddingNew(false);
    } catch (err) { 
      toast.error(err.message || 'Failed to save'); 
    }
  };

  const remove = async (id) => {
    setAssignments(prev => prev.filter(a => a.id !== id));
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('faculty_assignments').delete().eq('id', id).eq('team_id', team_id);
      if (error) throw error;
      toast.success('Assignment removed');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
      fetchData();
    }
  };

  const startAdd = () => {
    setEditingId(null); // Close any active edit cards
    setForm({
      faculty_id: '',
      subject_id: '',
      batch_ids: [],
      batch_id: '',
      hours_per_week: 3,
      min_slot_hours: 1.0,
      max_slot_hours: 2.0,
      color: 'blue',
      group_id: ''
    });
    setAddingNew(true);
  };

  const startEdit = (a) => {
    setAddingNew(false); // Close any active add panels
    setEditingId(a.id);
    setForm({
      faculty_id: a.faculty_id || '',
      subject_id: a.subject_id || '',
      batch_ids: [],
      batch_id: a.batch_id || '',
      hours_per_week: a.hours_per_week || 3,
      min_slot_hours: a.min_slot_hours || 1.0,
      max_slot_hours: a.max_slot_hours || 2.0,
      color: a.color || 'blue',
      group_id: a.group_id || ''
    });
  };

  const saveEdit = async (id) => {
    if (!form.faculty_id || !form.subject_id || !form.batch_id) { 
      toast.error('All fields required'); 
      return; 
    }
    
    // Optimistic UI Update
    setAssignments(prev => prev.map(a => a.id === id ? {
      ...a,
      faculty_id: form.faculty_id,
      subject_id: form.subject_id,
      batch_id: form.batch_id,
      hours_per_week: form.hours_per_week,
      min_slot_hours: form.min_slot_hours,
      max_slot_hours: form.max_slot_hours,
      color: form.color,
      group_id: form.group_id?.trim() || null
    } : a));
    
    setEditingId(null);
    
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase
        .from('faculty_assignments')
        .update({
          faculty_id: form.faculty_id,
          subject_id: form.subject_id,
          batch_id: form.batch_id,
          hours_per_week: form.hours_per_week,
          min_slot_hours: form.min_slot_hours,
          max_slot_hours: form.max_slot_hours,
          color: form.color || 'blue',
          group_id: form.group_id?.trim() || null
        })
        .eq('id', id)
        .eq('team_id', team_id);
        
      if (error) throw error;
      toast.success('Assignment updated successfully');
      await fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to update, please try again');
      fetchData();
    }
  };

  const closeAdd = () => {
    setAddingNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const grouped = {};
  assignments.forEach(a => {
    const key = a.faculty_id;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  });

  const getFacultyMaxHours = (facId) => {
    const f = faculty.find(ff => ff.id === facId);
    return f?.max_hours_per_day || 6;
  };

  const flatAssignments = useMemo(() => {
    const flat = [];
    const grp = {};
    assignments.forEach(a => {
      const key = a.faculty_id;
      if (!grp[key]) grp[key] = [];
      grp[key].push(a);
    });
    Object.values(grp).forEach(assigns => assigns.forEach(a => flat.push(a)));
    return flat;
  }, [assignments]);

  const { focusedIndex, setFocusedIndex, clearFocus } = useCardNavigation({
    itemCount: flatAssignments.length,
    enabled: !addingNew && !editingId && !loading,
    onSelect: useCallback((idx) => {
      const a = flatAssignments[idx];
      if (a) startEdit(a);
    }, [flatAssignments]),
  });

  useEscapeHierarchy([
    { id: 'add-panel', active: addingNew, onEscape: closeAdd },
    { id: 'edit-panel', active: !!editingId, onEscape: cancelEdit },
    { id: 'card-focus', active: focusedIndex !== null, onEscape: clearFocus },
  ]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <>
      <BulkImportModal
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityName="faculty_assignments"
        onImportComplete={fetchData}
      />
      <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-900/10" data-testid="assignments-panel">
        
        {/* Header Section */}
        <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-700/50 bg-white dark:bg-zinc-900 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 tracking-tight">Faculty Assignments</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{assignments.length} active assignments</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 px-2.5 flex items-center gap-1"
                onClick={() => setBulkImportOpen(true)}
                title="Bulk Import Assignments"
              >
                <Upload className="w-4 h-4" />
                <span>Import</span>
              </Button>
              <Button 
                size="sm" 
                className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" 
                onClick={startAdd} 
                data-testid="add-assignment-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Assignment
              </Button>
            </div>
          </div>

          {assignments.length > 0 && (
            <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800/50">
              <h4 className="text-[0.65rem] uppercase font-semibold text-zinc-400 tracking-widest mb-2">Color Legend</h4>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto pr-2 custom-scrollbar">
                {assignments.map(a => {
                  const assignColor = COLORS.find(c => c.value === (a.color || 'blue'));
                  return (
                    <div key={a.id} className="flex items-center gap-1.5 px-2 py-1 bg-zinc-50 dark:bg-zinc-800/50 rounded border border-zinc-200 dark:border-zinc-700/50">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: assignColor?.hex || '#4285f4' }} />
                      <span className="text-[0.65rem] font-medium text-zinc-600 dark:text-zinc-300 truncate max-w-[120px]" title={`${a.subject_name || '?'} (${a.batch_code || '?'})`}>
                        {a.subject_name || '?'} <span className="text-zinc-400">({a.batch_code || '?'})</span>
                      </span>
                      {a.group_id && (
                        <span className="text-[0.5rem] font-bold bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 px-1 py-0.5 rounded-full">G</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Group Manager */}
              {(() => {
                const grouped = {};
                assignments.forEach(a => { if (a.group_id) { if (!grouped[a.group_id]) grouped[a.group_id] = []; grouped[a.group_id].push(a); } });
                const groupEntries = Object.entries(grouped);
                if (groupEntries.length === 0) return null;
                return (
                  <div className="mt-3">
                    <button
                      onClick={() => setShowGroupManager(v => !v)}
                      className="flex items-center gap-1.5 text-[0.65rem] font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 transition-colors"
                    >
                      <Users2 className="w-3 h-3" />
                      {groupEntries.length} Group Lecture{groupEntries.length > 1 ? 's' : ''} configured
                      <ChevronDown className={cn('w-3 h-3 transition-transform', showGroupManager && 'rotate-180')} />
                    </button>
                    {showGroupManager && (
                      <div className="mt-2 space-y-2 animate-fade-in-up">
                        {groupEntries.map(([gid, members]) => (
                          <div key={gid} className="p-2.5 rounded-lg border border-violet-200 dark:border-violet-800/50 bg-violet-50/50 dark:bg-violet-900/10">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <Link2 className="w-3 h-3 text-violet-500" />
                                <span className="text-[0.65rem] font-bold text-violet-700 dark:text-violet-300 font-mono">{gid}</span>
                              </div>
                              <span className="text-[0.55rem] text-violet-500 bg-violet-100 dark:bg-violet-900/30 px-1.5 py-0.5 rounded-full">
                                {members.length} batches
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {members.map(m => (
                                <span key={m.id} className="text-[0.6rem] bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 px-2 py-0.5 rounded border border-violet-200 dark:border-violet-800/40 flex items-center gap-1">
                                  {m.subject_name} · {m.batch_code}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Content Section (Scrollable list where elements expand in-place) */}
        <ScrollArea className="flex-1 h-full">
          <div className="p-6 space-y-6 max-w-4xl mx-auto w-full">
            
            {/* 1. Add New Assignment Form (Expanded Inline at the top) */}
            {addingNew && (
              <div 
                className="p-6 rounded-2xl border-2 border-dashed border-blue-500/40 dark:border-blue-500/30 bg-white dark:bg-zinc-950 shadow-xl shadow-zinc-200/40 dark:shadow-none space-y-4 animate-in fade-in slide-in-from-top-4 duration-300"
              >
                <div className="flex items-center justify-between pb-3 border-b border-zinc-100 dark:border-zinc-800">
                  <div className="flex items-center gap-2">
                    <Plus className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Add New Faculty Assignment</span>
                  </div>
                  <button onClick={closeAdd} className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Faculty Selection */}
                  <div>
                    <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Faculty</Label>
                    <Select value={form.faculty_id} onValueChange={v => setForm(p => ({ ...p, faculty_id: v }))}>
                      <SelectTrigger className="mt-1 w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-9 text-xs">
                        <SelectValue placeholder="Select faculty" />
                      </SelectTrigger>
                      <SelectContent>
                        {faculty.map(f => (
                          <SelectItem key={f.id} value={f.id} className="text-xs">
                            {f.name} (max {f.max_hours_per_day}h/day)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subject Selection */}
                  <div>
                    <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Subject</Label>
                    <Select value={form.subject_id} onValueChange={v => setForm(p => ({ ...p, subject_id: v }))}>
                      <SelectTrigger className="mt-1 w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-9 text-xs">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map(s => (
                          <SelectItem key={s.id} value={s.id} className="text-xs">
                            {s.name} ({s.credit_hours}h)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Batches Selection (Inline checklist grouped by branch) */}
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Target Batches</Label>
                      <span className="text-[10px] text-zinc-400 font-bold">
                        {form.batch_ids.length} selected
                      </span>
                    </div>
                    
                    <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden bg-zinc-50 dark:bg-zinc-900/20">
                      <div className="p-2 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-white dark:bg-zinc-950 flex-shrink-0">
                        <button 
                          type="button" 
                          className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline font-bold uppercase tracking-wider"
                          onClick={() => {
                            const allBatchIds = batches.map(b => b.id);
                            const allSelected = batches.every(b => form.batch_ids.includes(b.id));
                            setForm(p => ({ ...p, batch_ids: allSelected ? [] : allBatchIds }));
                          }}
                        >
                          {batches.length > 0 && batches.every(b => form.batch_ids.includes(b.id)) ? "Deselect All" : "Select All"}
                        </button>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto p-3 space-y-4 custom-scrollbar bg-white dark:bg-zinc-950">
                        {branches.map(br => {
                          const branchBatches = batches.filter(bt => bt.branch_id === br.id);
                          if (branchBatches.length === 0) return null;
                          const allInBranch = branchBatches.every(bt => form.batch_ids.includes(bt.id));
                          
                          return (
                            <div key={br.id} className="space-y-1.5">
                              <div className="flex items-center justify-between px-1">
                                <span className="text-[0.65rem] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">
                                  {br.name}
                                </span>
                                <button 
                                  type="button"
                                  className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline font-semibold uppercase"
                                  onClick={() => {
                                    const otherBatchIds = form.batch_ids.filter(id => !branchBatches.some(bt => bt.id === id));
                                    if (allInBranch) {
                                      setForm(p => ({ ...p, batch_ids: otherBatchIds }));
                                    } else {
                                      setForm(p => ({ ...p, batch_ids: [...otherBatchIds, ...branchBatches.map(bt => bt.id)] }));
                                    }
                                  }}
                                >
                                  {allInBranch ? "Clear" : "All"}
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {branchBatches.map(bt => {
                                  const isSelected = form.batch_ids.includes(bt.id);
                                  return (
                                    <label key={bt.id} className={cn(
                                      "flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition-all border text-xs",
                                      isSelected 
                                        ? "bg-blue-600/5 dark:bg-blue-500/10 border-blue-200 dark:border-blue-800/60 font-medium" 
                                        : "hover:bg-zinc-50 dark:hover:bg-zinc-900 border-transparent bg-white dark:bg-zinc-950"
                                    )}>
                                      <input
                                        type="checkbox"
                                        className="w-3.5 h-3.5 rounded border-zinc-300 dark:border-zinc-700 text-blue-600 focus:ring-blue-500"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setForm(p => ({
                                            ...p,
                                            batch_ids: checked 
                                              ? [...p.batch_ids, bt.id]
                                              : p.batch_ids.filter(id => id !== bt.id)
                                          }));
                                        }}
                                      />
                                      <span className={cn(isSelected ? "text-blue-700 dark:text-blue-300 font-semibold" : "text-zinc-700 dark:text-zinc-300")}>
                                        {bt.name} <span className="text-[10px] text-zinc-400 font-mono">({bt.code})</span>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Hours inputs grid */}
                  <div className="grid grid-cols-3 gap-3 md:col-span-2">
                    <div>
                      <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Hrs/Week</Label>
                      <Input 
                        className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                        type="text" 
                        inputMode="numeric" 
                        value={form.hours_per_week} 
                        onChange={e => setForm(p => ({ ...p, hours_per_week: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} 
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Min Slot</Label>
                      <Input 
                        className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                        type="text" 
                        inputMode="decimal" 
                        value={form.min_slot_hours} 
                        onChange={e => setForm(p => ({ ...p, min_slot_hours: e.target.value === '' ? '' : (parseFloat(e.target.value) || 1) }))} 
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Max Slot</Label>
                      <Input 
                        className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                        type="text" 
                        inputMode="decimal" 
                        value={form.max_slot_hours} 
                        onChange={e => setForm(p => ({ ...p, max_slot_hours: e.target.value === '' ? '' : (parseFloat(e.target.value) || 2) }))} 
                      />
                    </div>
                  </div>
                </div>

                {/* Color Selector */}
                <div>
                  <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider block mb-1.5">Color Tag</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map(c => (
                      <button 
                        key={c.value} 
                        className={cn(
                          "w-5 h-5 rounded-full border transition-all hover:scale-110", 
                          form.color === c.value ? "border-zinc-800 dark:border-white scale-110 shadow-sm" : "border-transparent"
                        )}
                        style={{ background: c.hex }} 
                        onClick={() => setForm(f => ({ ...f, color: c.value }))} 
                      />
                    ))}
                  </div>
                </div>

                {/* Group Lecture Section */}
                <div className="rounded-xl border border-dashed border-violet-300 dark:border-violet-700/60 bg-violet-50/40 dark:bg-violet-900/10 p-3.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-3.5 h-3.5 text-violet-500" />
                    <Label className="text-[11px] font-bold text-violet-700 dark:text-violet-300 uppercase">Group Lecture (optional)</Label>
                  </div>
                  <p className="text-[0.6rem] text-violet-500 dark:text-violet-400 leading-normal">
                    Assign the same Group ID to multiple assignments to schedule them at the exact same time slot. Useful for combined-batch lectures.
                  </p>
                  <Input
                    value={form.group_id}
                    onChange={e => setForm(p => ({ ...p, group_id: e.target.value }))}
                    placeholder="e.g. group-maths-sem3"
                    className="h-8 text-xs font-mono border-violet-200 dark:border-violet-700 bg-white dark:bg-zinc-900 focus:ring-violet-400"
                  />
                  {form.group_id?.trim() && (
                    <p className="text-[0.6rem] text-violet-600 dark:text-violet-400">
                      ✓ Linked with group <strong>{form.group_id.trim()}</strong>
                    </p>
                  )}
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={closeAdd}>Cancel</Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs px-4" onClick={save}>Assign Faculty</Button>
                </div>
              </div>
            )}

            {/* 2. Main list of groups and assignments */}
            {Object.entries(grouped).map(([facId, assigns], gi) => {
              const fac = assigns[0];
              const totalHours = assigns.reduce((s, a) => s + a.hours_per_week, 0);
              const maxHours = getFacultyMaxHours(facId);
              const overloaded = totalHours > maxHours * 5;
              
              return (
                <div key={facId} className="animate-fade-in-up space-y-3" style={{ animationDelay: `${gi * 40}ms` }}>
                  
                  {/* Faculty Header */}
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-10 h-10 rounded-full bg-blue-600/10 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300 flex-shrink-0">
                      {(fac.faculty_name || '??').split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{fac.faculty_name || 'Unknown'}</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-zinc-400">{fac.faculty_department || ''} - {totalHours} hrs/week</p>
                        <Badge variant="secondary" className="text-[0.5rem]">Max {maxHours}h/day</Badge>
                        {overloaded && <Badge className="text-[0.5rem] bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400">Overloaded</Badge>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Cards container */}
                  <div className="ml-12 space-y-2">
                    {assigns.map(a => {
                      const isCurrentlyEditing = editingId === a.id;
                      const assignColor = COLORS.find(c => c.value === (a.color || 'blue'));
                      const flatIdx = flatAssignments.indexOf(a);
                      const isFocused = focusedIndex === flatIdx;
                      
                      return (
                        <React.Fragment key={a.id}>
                          {isCurrentlyEditing ? (
                            /* Expanded Card Inline Edit Form (Behaves as an in-place modal) */
                            <div 
                              className="p-5 rounded-xl border-2 border-blue-500/80 dark:border-blue-500/60 bg-white dark:bg-zinc-950 shadow-xl shadow-blue-500/5 dark:shadow-blue-500/10 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between pb-3 border-b border-zinc-100 dark:border-zinc-800">
                                <div className="flex items-center gap-2">
                                  <UserCog className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  <span className="text-xs font-bold text-zinc-800 dark:text-zinc-100 uppercase tracking-wider">Edit Assignment</span>
                                </div>
                                <button onClick={cancelEdit} className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                                  <X className="w-4 h-4 text-zinc-400" />
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Faculty */}
                                <div>
                                  <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Faculty</Label>
                                  <Select value={form.faculty_id} onValueChange={v => setForm(p => ({ ...p, faculty_id: v }))}>
                                    <SelectTrigger className="mt-1 w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-9 text-xs">
                                      <SelectValue placeholder="Select faculty" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {faculty.map(f => (
                                        <SelectItem key={f.id} value={f.id} className="text-xs">
                                          {f.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Subject */}
                                <div>
                                  <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Subject</Label>
                                  <Select value={form.subject_id} onValueChange={v => setForm(p => ({ ...p, subject_id: v }))}>
                                    <SelectTrigger className="mt-1 w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-9 text-xs">
                                      <SelectValue placeholder="Select subject" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {subjects.map(s => (
                                        <SelectItem key={s.id} value={s.id} className="text-xs">
                                          {s.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Batch */}
                                <div>
                                  <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Batch</Label>
                                  <Select value={form.batch_id} onValueChange={v => setForm(p => ({ ...p, batch_id: v }))}>
                                    <SelectTrigger className="mt-1 w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-9 text-xs">
                                      <SelectValue placeholder="Select batch" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {batches.map(b => (
                                        <SelectItem key={b.id} value={b.id} className="text-xs">
                                          {b.name} ({b.code})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Hours Inputs */}
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Hrs/Week</Label>
                                    <Input 
                                      className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                                      type="text" 
                                      inputMode="numeric" 
                                      value={form.hours_per_week} 
                                      onChange={e => setForm(p => ({ ...p, hours_per_week: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} 
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Min Slot</Label>
                                    <Input 
                                      className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                                      type="text" 
                                      inputMode="decimal" 
                                      value={form.min_slot_hours} 
                                      onChange={e => setForm(p => ({ ...p, min_slot_hours: e.target.value === '' ? '' : (parseFloat(e.target.value) || 1) }))} 
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Max Slot</Label>
                                    <Input 
                                      className="mt-1 h-9 text-xs bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" 
                                      type="text" 
                                      inputMode="decimal" 
                                      value={form.max_slot_hours} 
                                      onChange={e => setForm(p => ({ ...p, max_slot_hours: e.target.value === '' ? '' : (parseFloat(e.target.value) || 2) }))} 
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Color Selector */}
                              <div>
                                <Label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider block mb-1.5">Color Tag</Label>
                                <div className="flex flex-wrap gap-2">
                                  {COLORS.map(c => (
                                    <button 
                                      key={c.value} 
                                      className={cn(
                                        "w-5 h-5 rounded-full border transition-all hover:scale-110", 
                                        form.color === c.value ? "border-zinc-800 dark:border-white scale-110 shadow-sm" : "border-transparent"
                                      )}
                                      style={{ background: c.hex }} 
                                      onClick={() => setForm(f => ({ ...f, color: c.value }))} 
                                    />
                                  ))}
                                </div>
                              </div>

                              {/* Group Lecture Section */}
                              <div className="rounded-xl border border-dashed border-violet-300 dark:border-violet-700/60 bg-violet-50/40 dark:bg-violet-900/10 p-3.5 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Link2 className="w-3.5 h-3.5 text-violet-500" />
                                  <Label className="text-[11px] font-bold text-violet-700 dark:text-violet-300 uppercase">Group Lecture (optional)</Label>
                                </div>
                                <Input
                                  value={form.group_id}
                                  onChange={e => setForm(p => ({ ...p, group_id: e.target.value }))}
                                  placeholder="e.g. group-maths-sem3"
                                  className="h-8 text-xs font-mono border-violet-200 dark:border-violet-700 bg-white dark:bg-zinc-900 focus:ring-violet-400"
                                />
                              </div>

                              {/* Action Footer */}
                              <div className="flex items-center justify-between pt-3 border-t border-zinc-100 dark:border-zinc-800">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 text-xs font-semibold h-8"
                                  onClick={() => {
                                    if (confirm("Are you sure you want to delete this assignment?")) {
                                      remove(a.id);
                                      cancelEdit();
                                    }
                                  }}
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Assignment
                                </Button>
                                <div className="flex items-center gap-2">
                                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={cancelEdit}>Cancel</Button>
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs px-4" onClick={() => saveEdit(a.id)}>Update Assignment</Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Read-Only Mode Card */
                            <div 
                              className={cn(
                                "flex items-center justify-between p-2.5 rounded-lg border group transition-all cursor-pointer bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700/80 hover:border-blue-400 dark:hover:border-zinc-600 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/80",
                                isFocused && "ring-2 ring-violet-500/50 shadow-md"
                              )} 
                              onClick={() => startEdit(a)}
                              data-testid={`assignment-card-${a.id}`} 
                              data-card-index={flatIdx}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: assignColor?.hex || '#4285f4' }} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200 truncate">{a.subject_name || 'Unknown Subject'}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <Badge variant="secondary" className="text-[0.55rem] h-4">{a.batch_code || a.batch_name || '?'}</Badge>
                                    <span className="text-[0.6rem] text-zinc-400 font-medium">
                                      {a.hours_per_week} hrs/week ({a.min_slot_hours}-{a.max_slot_hours}h slots)
                                    </span>
                                  </div>
                                  {a.group_id && (
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <Link2 className="w-2.5 h-2.5 text-violet-500" />
                                      <span className="text-[0.55rem] text-violet-600 dark:text-violet-400 font-mono">{a.group_id}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                <button 
                                  className="w-7 h-7 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-300 hover:text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(a);
                                  }} 
                                  data-testid={`edit-assignment-${a.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button 
                                  className="w-7 h-7 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center text-zinc-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (confirm("Are you sure you want to delete this assignment?")) {
                                      remove(a.id);
                                    }
                                  }} 
                                  data-testid={`delete-assignment-${a.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            
            {assignments.length === 0 && !addingNew && (
              <p className="text-sm text-zinc-400 text-center py-12">No assignments yet. Create faculty-subject-batch mappings.</p>
            )}
          </div>
        </ScrollArea>

      </div>
    </>
  );
}
